/**
 * @voltxampere/web — UI signals (Sprint 11).
 *
 * Reactive state for the UI shell. Pure Preact Signals, no
 * coupling to Lit. Components subscribe via `SignalController`
 * (see `lit-utils/signal-controller.ts`) so the same store
 * can later drive non-Lit consumers (devtools panel, e2e
 * helpers).
 *
 * Keep this file *only* about UI ephemera (modal open flags,
 * search query, the symbol the user has armed). Persistent
 * application state — the circuit itself, undo history,
 * cursor — lives in dedicated stores so we can wipe UI state
 * without losing edits.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import type { ComponentSymbol } from "@voltxampere/ui";

/** Free-text query bound to the sidebar search input. */
export const sidebarSearchQuery = signal<string>("");

/**
 * The symbol the user has "armed" — Sprint 13 will consume
 * this to drop the symbol on the canvas at the next click.
 * Sprint 11 only logs the arm event.
 */
export const armedSymbol = signal<ComponentSymbol | null>(null);

/** Cmd+K command palette open flag. */
export const commandPaletteOpen = signal<boolean>(false);

/** `?` shortcut overlay open flag. */
export const shortcutOverlayOpen = signal<boolean>(false);

export type Tool = "select" | "wire" | "erase";

/** Current canvas tool. Sprint 13 will wire this to the canvas. */
export const selectedTool = signal<Tool>("select");

/** Number of currently-selected entities — Sprint 13 will populate. */
export const selectionCount = signal<number>(0);
